import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", {
  day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
}) : "—");

/**
 * Sep 2026 — resend an inspection report.
 *
 * The endpoint existed but nothing called it, so a report could never be
 * resent (e.g. after correcting a wrong email). Approved reports only —
 * the backend enforces that too. Always goes to the customer's stored
 * address; the offer can be included or left out per send.
 */
export function ResendReportButton({ campaignId, inspection, onSent, label = "Resend report" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [includeOffer, setIncludeOffer] = useState(true);
  const [sends, setSends] = useState(inspection.report_email_sends || []);

  const approved = inspection.review_status === "approved"
    || (inspection.review_status == null && inspection.report_email_status === "sent");
  if (!approved || !inspection.email) return null;

  const lastSent = sends.length
    ? sends[sends.length - 1]
    : (inspection.report_email_status === "sent"
      ? { at: inspection.report_email_sent_at, to: inspection.email, by: "auto-send" }
      : null);

  const send = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/property-campaigns/${campaignId}/inspections/${inspection.id}/retry-report-email`,
        { include_offer: includeOffer });
      setSends(data.report_email_sends || []);
      if (data.report_email_status === "sent") {
        toast.success(`Report sent to ${inspection.email}`);
        setOpen(false);
        onSent?.(data);
      } else {
        toast.error(data.report_email_error || "The email provider rejected the send.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend the report.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900"
          data-testid={`resend-report-open-${inspection.id}`}
        >
          <Send className="w-3.5 h-3.5" /> {label}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="resend-report-dialog">
        <DialogHeader>
          <DialogTitle>Resend inspection report</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Sending to</div>
            <div className="font-semibold text-slate-900" data-testid="resend-report-recipient">
              {inspection.email}
            </div>
            <div className="text-xs text-slate-500 mt-1">{inspection.street_address}</div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeOffer}
              onChange={(e) => setIncludeOffer(e.target.checked)}
              className="mt-0.5"
              data-testid="resend-report-include-offer"
            />
            <span className="text-slate-700">
              Include the 20% lab test offer
              <span className="block text-xs text-slate-500">
                Left out automatically if the offer has expired.
              </span>
            </span>
          </label>

          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">
              Send history
            </div>
            {lastSent ? (
              <ul className="space-y-1" data-testid="resend-report-history">
                {(sends.length ? sends : [lastSent]).slice().reverse().map((s, idx) => (
                  <li key={idx} className="text-xs text-slate-600 flex flex-wrap gap-x-2">
                    <span className="font-semibold text-slate-800">{fmt(s.at)}</span>
                    <span>→ {s.to}</span>
                    {s.by && <span className="text-slate-400">by {s.by}</span>}
                    {s.included_offer === false && <span className="text-slate-400">no offer</span>}
                    {s.status && s.status !== "sent" && (
                      <span className="text-rose-600 font-semibold">{s.status}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500" data-testid="resend-report-history-empty">
                Not sent yet.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={send} disabled={busy} data-testid="resend-report-send">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {busy ? "Sending…" : "Send report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ResendReportButton;
